import { createEntityAdapter, createSlice } from '@reduxjs/toolkit';
import { AsyncEntity } from 'src/global';
import createAsyncThunkFromFetch from 'src/store/middlewares/createAsyncThunkFromFetch';
import {
  addCollectionsToTemplate as addCollectionsToTemplateAPI,
  addGroupToTemplateAPI,
  addTemplateCompanyAPI,
  createTemplate as createTemplateApi,
  deleteTemplate as deleteTemplateApi,
  getTemplate,
  getTemplateCollections as getTemplateCollectionsAPI,
  getTemplateCompaniesAPI,
  getTemplateGroupsAPI,
  getTemplates,
  removeCollectionsFromTemplate as removeCollectionsFromTemplateAPI,
  removeCompanyFromTemplateAPI,
  removeGroupFromTemplateAPI,
  updateTemplate as updateCollectionApi,
  updateTemplateCollectionsOrder as updateTemplateCollectionsOrderAPI,
} from 'src/features/templates/templates.api';
import { ContentTemplate } from 'src/features/templates/templates.types';
import { Collection } from 'src/features/collections/collections.types';

const name = 'templates';

const templateEntityAdapter = createEntityAdapter<AsyncEntity<ContentTemplate>>({
  selectId: template => template.id,
});

export const templatesSelectors = templateEntityAdapter.getSelectors();

export const fetchTemplates = createAsyncThunkFromFetch(`${name}/fetchTemplates`, getTemplates);
export const fetchTemplate = createAsyncThunkFromFetch(`${name}/fetchTemplate`, getTemplate);
export const deleteTemplate = createAsyncThunkFromFetch(`${name}/deleteTemplate`, deleteTemplateApi);
export const createTemplate = createAsyncThunkFromFetch(`${name}/createTemplate`, createTemplateApi);
export const updateTemplate = createAsyncThunkFromFetch(`${name}/updateTemplate`, updateCollectionApi);

export const fetchTemplateCollections = createAsyncThunkFromFetch(
  `${name}/fetchTemplateCollections`,
  getTemplateCollectionsAPI
);
export const addCollectionsToTemplate = createAsyncThunkFromFetch(
  `${name}/addCollectionsToTemplate`,
  addCollectionsToTemplateAPI
);
export const removeCollectionsFromTemplate = createAsyncThunkFromFetch(
  `${name}/removeCollectionsFromTemplate`,
  removeCollectionsFromTemplateAPI
);
export const updateTemplateCollectionsOrder = createAsyncThunkFromFetch(
  `${name}/updateTemplateCollectionsOrder`,
  updateTemplateCollectionsOrderAPI
);
export const fetchTemplateCompanies = createAsyncThunkFromFetch(
  `${name}/fetchTemplateCompanies`,
  getTemplateCompaniesAPI
);
export const addTemplateCompany = createAsyncThunkFromFetch(`${name}/addTemplateCompany`, addTemplateCompanyAPI);
export const removeCompanyFromTemplate = createAsyncThunkFromFetch(
  `${name}/removeCompanyFromTemplate`,
  removeCompanyFromTemplateAPI
);
export const fetchTemplateGroups = createAsyncThunkFromFetch(`${name}/fetchTemplateGroups`, getTemplateGroupsAPI);
export const addGroupToTemplate = createAsyncThunkFromFetch(`${name}/addGroupToTemplate`, addGroupToTemplateAPI);
export const removeGroupFromTemplate = createAsyncThunkFromFetch(
  `${name}/removeGroupFromTemplate`,
  removeGroupFromTemplateAPI
);

const templateSlice = createSlice({
  name,
  initialState: templateEntityAdapter.getInitialState({
    isPending: false,
    hasError: false,
  }),
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchTemplates.pending, state => {
      state.isPending = true;
      state.hasError = false;
    });
    builder.addCase(fetchTemplates.fulfilled, (state, { payload }) => {
      templateEntityAdapter.setAll(state, payload);
      state.isPending = false;
      state.hasError = false;
    });
    builder.addCase(fetchTemplate.fulfilled, (state, { payload }) => {
      templateEntityAdapter.upsertOne(state, payload);
    });
    builder.addCase(fetchTemplates.rejected, state => {
      state.isPending = false;
      state.hasError = true;
    });
    builder.addCase(deleteTemplate.pending, (state, { meta }) => {
      templateEntityAdapter.updateOne(state, {
        id: meta.arg.templateId,
        changes: { isPending: true },
      });
    });
    builder.addCase(deleteTemplate.fulfilled, (state, { meta }) => {
      templateEntityAdapter.removeOne(state, meta.arg.templateId);
    });
    builder.addCase(createTemplate.fulfilled, (state, { payload }) => {
      templateEntityAdapter.upsertOne(state, payload);
    });
    builder.addCase(updateTemplate.fulfilled, (state, { payload }) => {
      templateEntityAdapter.upsertOne(state, payload);
    });
    builder.addCase(addCollectionsToTemplate.fulfilled, (state, { meta, payload }) => {
      const template = templatesSelectors.selectById(state, meta.arg.templateId) as ContentTemplate;
      templateEntityAdapter.updateOne(state, {
        id: meta.arg.templateId,
        changes: {
          videos: {
            collections: [...template.videos.collections, ...payload],
            order: [...template.videos.order, ...payload.map(p => p.id)],
          },
        },
      });
    });
    builder.addCase(removeCollectionsFromTemplate.fulfilled, (state, { meta }) => {
      const template = templatesSelectors.selectById(state, meta.arg.templateId) as ContentTemplate;
      const collections = template.videos.collections.filter(c => !meta.arg.data.ids.includes(c.id));
      const order = template.videos.order.filter(c => !meta.arg.data.ids.includes(c));
      templateEntityAdapter.updateOne(state, {
        id: meta.arg.templateId,
        changes: {
          videos: {
            collections,
            order,
          },
        },
      });
    });
    builder.addCase(fetchTemplateCollections.fulfilled, (state, { meta, payload }) => {
      templateEntityAdapter.updateOne(state, {
        id: meta.arg.templateId,
        changes: {
          videos: payload,
        },
      });
    });
    builder.addCase(updateTemplateCollectionsOrder.fulfilled, (state, { meta, payload }) => {
      const template = templatesSelectors.selectById(state, meta.arg.templateId) as ContentTemplate;
      const collections = payload.ids.map(s => template.videos.collections.find(c => c.id === s)) as Collection[];
      templateEntityAdapter.updateOne(state, {
        id: meta.arg.templateId,
        changes: {
          videos: {
            collections,
            order: payload.ids,
          },
        },
      });
    });
    builder.addCase(addTemplateCompany.fulfilled, (state, { meta, payload }) => {
      const template = templatesSelectors.selectById(state, meta.arg.templateId) as ContentTemplate;
      templateEntityAdapter.updateOne(state, {
        id: meta.arg.templateId,
        changes: {
          companies: [...template.companies, payload],
        },
      });
    });
    builder.addCase(removeCompanyFromTemplate.fulfilled, (state, { meta }) => {
      const template = templatesSelectors.selectById(state, meta.arg.templateId) as ContentTemplate;
      templateEntityAdapter.updateOne(state, {
        id: meta.arg.templateId,
        changes: {
          companies: template.companies.filter(c => c.id !== meta.arg.companyId),
        },
      });
    });
    builder.addCase(addGroupToTemplate.fulfilled, (state, { meta, payload }) => {
      const template = templatesSelectors.selectById(state, meta.arg.templateId) as ContentTemplate;
      templateEntityAdapter.updateOne(state, {
        id: meta.arg.templateId,
        changes: {
          groups: [...template.groups, payload],
        },
      });
    });
    builder.addCase(removeGroupFromTemplate.fulfilled, (state, { meta }) => {
      const template = templatesSelectors.selectById(state, meta.arg.templateId) as ContentTemplate;
      templateEntityAdapter.updateOne(state, {
        id: meta.arg.templateId,
        changes: {
          groups: template.groups.filter(c => c.id !== meta.arg.groupId),
        },
      });
    });
  },
});

const { reducer } = templateSlice;

export default reducer;
